import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { SuperAdminPageHelp } from '@/components/SuperAdmin/SuperAdminPageHelp';
import { ProjectSearchSelect } from '@/components/ProjectSearchSelect';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';
import {
  superZipatoAnalysisApi,
  SuperZipatoAttributesResponse,
  SuperZipatoDevicesResponse,
} from '@/api/superZipatoAnalysisApi';

type Mode = 'devices' | 'attributes';

function getErrorMessage(err: unknown): string {
  if (!err) return 'Erreur';
  if (typeof err === 'object' && err !== null && 'body' in err) {
    const body = (err as { body?: unknown }).body;
    if (typeof body === 'object' && body !== null && 'message' in body) {
      const msg = (body as { message?: unknown }).message;
      if (typeof msg === 'string' && msg) return msg;
    }
  }
  if (err instanceof Error) return err.message || 'Erreur';
  return 'Erreur';
}

export function SuperZipatoAnalysisPage() {
  const { user } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();

  const [selectedProjectIri, setSelectedProjectIri] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [mode, setMode] = useState<Mode>('devices');
  const [devicesResult, setDevicesResult] = useState<SuperZipatoDevicesResponse | null>(null);
  const [attributesResult, setAttributesResult] = useState<SuperZipatoAttributesResponse | null>(null);

  const platformProjectId = useMemo(() => {
    if (!selectedProjectIri) return null;
    const n = parseInt(selectedProjectIri.replace('/api/projects/', ''), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [selectedProjectIri]);

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const analyseDevicesMutation = useMutation({
    mutationFn: async () => {
      if (!platformProjectId) throw new Error('Please select a project');
      return superZipatoAnalysisApi.analyseDevices(platformProjectId);
    },
    onMutate: () => {
      setError('');
      setMode('devices');
      setDevicesResult(null);
    },
    onSuccess: (data) => setDevicesResult(data),
    onError: (e) => setError(getErrorMessage(e)),
  });

  const analyseAttributesMutation = useMutation({
    mutationFn: async () => {
      if (!platformProjectId) throw new Error('Please select a project');
      return superZipatoAnalysisApi.analyseAttributes(platformProjectId);
    },
    onMutate: () => {
      setError('');
      setMode('attributes');
      setAttributesResult(null);
    },
    onSuccess: (data) => setAttributesResult(data),
    onError: (e) => setError(getErrorMessage(e)),
  });

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Super utilisateur</span> / Zipato Analysis
          </div>
          <div className="flex items-center gap-4">
            <SuperAdminPageHelp />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              <span className="material-icons-outlined">{isDark ? 'light_mode' : 'dark_mode'}</span>
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <span className="material-icons-outlined text-lg">person</span>
              <span>{user?.fullName || 'Admin'}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Zipato Analysis (read-only)</h1>

            <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Platform Project</label>
                <ProjectSearchSelect value={selectedProjectIri} onChange={setSelectedProjectIri} />
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  disabled={!platformProjectId || analyseDevicesMutation.isPending || analyseAttributesMutation.isPending}
                  onClick={() => analyseDevicesMutation.mutate()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
                >
                  <span className="material-icons-outlined text-lg">devices</span>
                  Analyse devices
                </button>

                <button
                  type="button"
                  disabled={!platformProjectId || analyseDevicesMutation.isPending || analyseAttributesMutation.isPending}
                  onClick={() => analyseAttributesMutation.mutate()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-700 transition-colors dark:bg-slate-700 dark:hover:bg-slate-600"
                >
                  <span className="material-icons-outlined text-lg">schema</span>
                  Analyse attributes
                </button>

                {(analyseDevicesMutation.isPending || analyseAttributesMutation.isPending) && (
                  <span className="text-sm text-slate-500 dark:text-slate-400">Analyse en cours...</span>
                )}
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}
            </div>

            {mode === 'devices' && devicesResult && (
              <div className="mt-6 space-y-4">
                <SummaryCard
                  title="Devices summary"
                  lines={[
                    `zipatoDevices=${devicesResult.global.zipatoDevicesCount}`,
                    `dbDevices=${devicesResult.global.dbDevicesCount}`,
                    `toImport=${devicesResult.global.toImportCount}`,
                    `toDelete=${devicesResult.global.toDeleteCount}`,
                    `nameMismatch=${devicesResult.global.nameMismatchCount}`,
                  ]}
                />

                {devicesResult.boxes.map((b) => (
                  <div
                    key={b.boxId}
                    className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl overflow-hidden"
                  >
                    <div className="px-5 py-4 border-b border-slate-200 dark:border-[#1E293B]">
                      <div className="font-semibold text-slate-900 dark:text-white">
                        {b.boxName} <span className="text-xs text-slate-500">({b.boxUuid})</span>
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        zipatoDevices={b.zipatoDevicesCount} — dbDevices={b.dbDevicesCount}
                      </div>
                    </div>
                    <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <MiniList title={`À importer (${b.toImport.length})`} rows={b.toImport.map((d) => `${d.name ?? 'N/A'} (${d.uuid})`)} />
                      <MiniList title={`À supprimer (${b.toDelete.length})`} rows={b.toDelete.map((d) => `${d.name} (${d.uuid})`)} />
                      <MiniList
                        title={`Name mismatch (${b.nameMismatch.length})`}
                        rows={b.nameMismatch.map((m) => `${m.uuid}: db="${m.dbName}" zipato="${m.zipatoName}"`)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {mode === 'attributes' && attributesResult && (
              <div className="mt-6 space-y-4">
                <SummaryCard
                  title="Attributes summary"
                  lines={[
                    `zipatoAttributes=${attributesResult.global.zipatoAttributesCount}`,
                    `dbAttributes=${attributesResult.global.dbAttributesCount}`,
                    `missingOnDb=${attributesResult.global.missingOnDbCount}`,
                    `extraOnDb=${attributesResult.global.extraOnDbCount}`,
                    `deviceMismatch=${attributesResult.global.deviceMismatchCount}`,
                  ]}
                />

                {attributesResult.boxes.map((b) => (
                  <div
                    key={b.boxId}
                    className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl overflow-hidden"
                  >
                    <div className="px-5 py-4 border-b border-slate-200 dark:border-[#1E293B]">
                      <div className="font-semibold text-slate-900 dark:text-white">
                        {b.boxName} <span className="text-xs text-slate-500">({b.boxUuid})</span>
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        zipatoAttributes={b.zipatoAttributesCount} — dbAttributes={b.dbAttributesCount}
                      </div>
                    </div>

                    <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <MiniList
                        title={`Missing on DB (${b.missingOnDb.length})`}
                        rows={b.missingOnDb.map((a) => `${a.deviceName ?? 'N/A'} :: ${a.attributeTypeCode ?? 'UNKNOWN'} (${a.uuid})`)}
                      />
                      <MiniList
                        title={`Extra on DB (${b.extraOnDb.length})`}
                        rows={b.extraOnDb.map((a) => `${a.deviceName} :: ${a.name} (${a.uuid})`)}
                      />
                      <MiniList
                        title={`Device mismatch (${b.deviceMismatch.length})`}
                        rows={b.deviceMismatch.map(
                          (m) => `${m.uuid}: db="${m.dbDeviceName}" (${m.dbDeviceUuid}) zipato="${m.zipatoDeviceName ?? 'N/A'}" (${m.zipatoDeviceUuid})`
                        )}
                      />
                    </div>

                    {b.devices.length > 0 && (
                      <div className="px-5 pb-5">
                        <div className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
                          Per-device diffs ({b.devices.length})
                        </div>
                        <div className="space-y-3">
                          {b.devices.map((d) => (
                            <div key={d.deviceUuid} className="rounded-lg border border-slate-200 dark:border-[#1E293B] p-4">
                              <div className="text-sm font-medium text-slate-900 dark:text-white">
                                {d.deviceNameZipato ?? d.deviceNameDb ?? 'Device'}{' '}
                                <span className="text-xs text-slate-500">({d.deviceUuid})</span>
                              </div>
                              <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-3">
                                <MiniList
                                  title={`Missing on DB (${d.missingOnDb.length})`}
                                  rows={d.missingOnDb.map((a) => `${a.attributeTypeCode ?? 'UNKNOWN'} (${a.uuid})`)}
                                  compact
                                />
                                <MiniList
                                  title={`Extra on DB (${d.extraOnDb.length})`}
                                  rows={d.extraOnDb.map((a) => `${a.name} (${a.uuid})`)}
                                  compact
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function SummaryCard({ title, lines }: { title: string; lines: string[] }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A] p-5">
      <div className="font-semibold text-slate-900 dark:text-white">{title}</div>
      <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
        {lines.map((l) => (
          <div key={l}>{l}</div>
        ))}
      </div>
    </div>
  );
}

function MiniList({
  title,
  rows,
  compact = false,
}: {
  title: string;
  rows: string[];
  compact?: boolean;
}) {
  return (
    <div className={`rounded-lg border border-slate-200 dark:border-[#1E293B] ${compact ? 'p-3' : 'p-4'}`}>
      <div className="text-sm font-semibold text-slate-900 dark:text-white">{title}</div>
      {rows.length === 0 ? (
        <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">—</div>
      ) : (
        <ul className="mt-2 space-y-1 text-xs text-slate-700 dark:text-slate-200">
          {rows.slice(0, 200).map((r, i) => (
            <li key={i} className="break-all">
              {r}
            </li>
          ))}
          {rows.length > 200 && (
            <li className="text-slate-500 dark:text-slate-400">… +{rows.length - 200} more</li>
          )}
        </ul>
      )}
    </div>
  );
}

